/**
 * Cliente HTTP da conferência facial (E80.F2, ADR-0079) — raia própria.
 *
 * Espelha apps/api/types/conferencia_facial.py e
 * apps/api/runtime/conferencia_facial.py.
 *
 * O contrato não traz score calculado nem distância entre rostos (§2.3). O que
 * traz desde a E90.F4 (emenda E.9) é a `compatibilidade` do par: um DEGRAU verbal
 * holístico, numa escala simétrica desde a E106.F3 (emenda E.11) e sem número —
 * nenhuma função o calcula das linhas do painel.
 *
 * A `leitura` vem RESOLVIDA do servidor: `null` é "ainda não conferido",
 * `nao_se_aplica` é "não há par escolhido" e `nao_demonstrada` (E216.F4.C05) é a comparação que
 * usou uma foto que saiu do caso, sem outro par apto. Derivá-la aqui deixaria a tela e o
 * backend livres para discordar sobre o mesmo estado (lição do veredito de
 * fonte única, E59).
 */
import type { AxiosInstance } from 'axios';

import { classifyExameEletronicoError } from '@/lib/exame_eletronico';
import type { GrupoDaFoto } from '@/lib/fotos_do_signatario';

/** O que uma conferência gravada pode dizer. */
export type LeituraDoParecer =
  | 'elementos_convergentes'
  | 'elementos_divergentes'
  | 'nao_foi_possivel_conferir';

/** O vocabulário completo da tela: o do parecer mais a ausência declarada. */
export type LeituraDaConferencia = LeituraDoParecer | 'nao_se_aplica' | 'nao_demonstrada';

export interface FotoConferidaData {
  foto_id: string;
  grupo: GrupoDaFoto;
  nome_arquivo: string;
  content_sha256: string;
  /**
   * E122.F2/E123.F2 (ADR-0079 E.23/E.24): a foto entrou no par pela escolha do sistema, e não do
   * perito. Opcional na leitura: o parecer anterior à marca não a traz.
   */
  confirmacao_automatica?: boolean;
}

export interface ParConferidoData {
  documento_identidade: FotoConferidaData;
  ccb: FotoConferidaData;
}

/** Os nove elementos do parecer anterior à E172.F4, lido como foi emitido (emenda E87 §E.1). */
export const ELEMENTOS_DO_PAINEL_V1 = [
  'formato_do_rosto',
  'testa',
  'sobrancelhas',
  'olhos_regiao_periocular',
  'nariz',
  'labios_boca',
  'queixo_mandibula',
  'proporcao_olhos_nariz_boca',
  'expressao_facial',
] as const;

/**
 * Os elementos do painel, na ordem em que ele é lido: os nove de antes e os componentes da lista
 * FISWG adotada que faltavam (E172.F4, GUS-230 B2.3).
 */
export const ELEMENTOS_DO_PAINEL = [
  ...ELEMENTOS_DO_PAINEL_V1,
  'pele',
  'cabelo',
  'bochechas',
  'orelhas',
  'pescoco',
  'pelos_faciais',
  'linhas_faciais',
  'cicatrizes',
  'marcas_faciais',
  'alteracoes',
] as const;

export type ElementoDoPainel = (typeof ELEMENTOS_DO_PAINEL)[number];

export type NivelDoFator = 'adequado' | 'limitado' | 'inadequado';
export type AdequacaoDaImagem = 'adequada' | 'limitada' | 'inadequada';
export const FATORES_DE_QUALIDADE = [
  'resolucao',
  'nitidez',
  'iluminacao',
  'pose',
  'oclusao',
] as const;
export type FatorDeQualidade = (typeof FATORES_DE_QUALIDADE)[number];

/** E172.F4 (B2.3): a qualidade e a adequação de uma imagem, avaliadas antes da comparação. */
export interface QualidadeDaImagemData {
  resolucao: NivelDoFator;
  nitidez: NivelDoFator;
  iluminacao: NivelDoFator;
  pose: NivelDoFator;
  oclusao: NivelDoFator;
  adequacao: AdequacaoDaImagem;
  observacao: string;
}

export interface QualidadeDoParData {
  documento_identidade: QualidadeDaImagemData;
  ccb: QualidadeDaImagemData;
}

/** Os itens que o assistente técnico verifica na qualidade, antes dos componentes. */
export const ITENS_DE_QUALIDADE = ['qualidade_documento_identidade', 'qualidade_ccb'] as const;

/** Escala VERBAL e fechada — sem percentual, score ou distância (§2.3/§2.5). */
/**
 * `nao_validado` (ADR-0079 E.12, E109.F1): o elemento em que as três leituras independentes não
 * coincidiram. Nunca vem do agente; é a consolidação dizendo que aquela linha não foi validada.
 */
export const GRAUS_DA_LINHA = [
  'alta',
  'media_alta',
  'media',
  'baixa',
  'nao_avaliavel',
  'nao_validado',
] as const;

export type GrauDaLinha = (typeof GRAUS_DA_LINHA)[number];

/**
 * O degrau da CONCLUSÃO do painel — o conjunto, não uma linha (emenda E.9).
 *
 * E106.F3 (emenda E.11, GT-0054 I-17): a escala é simétrica — apoio à mesma
 * origem em três forças, inconclusivo, apoio a origem distinta em três forças
 * e não avaliável. Os quatro degraus da escala anterior só chegam em parecer
 * gravado antes dela, e a tela os nomeia como o que são. Sem percentual: o
 * número saiu do documento na E106.F1 e da tela aqui.
 */
export const COMPATIBILIDADES_DO_PAR = [
  'mesma_origem_forte',
  'mesma_origem_moderado',
  'mesma_origem_limitado',
  'inconclusivo',
  'origem_distinta_limitado',
  'origem_distinta_moderado',
  'origem_distinta_forte',
  'nao_avaliavel',
  'alta',
  'media_alta',
  'media',
  'baixa',
] as const;

export type CompatibilidadeDoPar = (typeof COMPATIBILIDADES_DO_PAR)[number];

export interface LinhaDoPainelData {
  elemento: ElementoDoPainel;
  /** O que se observa do elemento na foto colhida na formalização. */
  selfie: string;
  /** O que se observa do mesmo elemento no retrato do documento. */
  identidade: string;
  grau: GrauDaLinha;
}

export interface ConferenciaFacialParecerData {
  id: string;
  case_id: string;
  created_by: string;
  par: ParConferidoData;
  /**
   * As nove linhas do painel, nesta ordem — ou VAZIO.
   *
   * Vazio não é frouxidão: é o parecer anterior à migration 0086 e o da
   * discordância entre as K execuções gravado antes da E109.F1 (desde a emenda
   * E.12, a discordância traz o painel por elemento, com `nao_validado` onde as
   * leituras não coincidiram). As duas formas vêm do servidor, e a guarda aceita
   * exatamente essas duas.
   */
  painel: LinhaDoPainelData[];
  leitura: LeituraDoParecer;
  /**
   * O degrau da CONCLUSÃO, ou `null` no parecer anterior à migration 0092 —
   * ausência declarada na tela, nunca degrau inventado.
   */
  compatibilidade: CompatibilidadeDoPar | null;
  convergencias: string[];
  divergencias: string[];
  ressalvas: string[];
  created_at: string;
  /**
   * Se as três leituras independentes coincidiram na leitura global (ADR-0079 E.12); `null` no
   * parecer gravado sem esse registro. Opcional na leitura: o parecer antigo não o traz.
   */
  leituras_coincidiram?: boolean | null;
  /**
   * Quantas das K leituras sustentam cada ponto (ADR-0079 E.13, E110.F1); `null` no parecer
   * consolidado antes da maioria. Opcional na leitura: o parecer antigo não o traz.
   */
  sustentacao?: SustentacaoDasLeiturasData | null;
  /**
   * As leituras registraram cor de íris, cabelo ou pele (F-E116.F2-01): resolvido no servidor,
   * nunca na tela. Opcional na leitura: a resposta do parecer recém-gravado não o traz.
   */
  cor_na_leitura?: boolean;
  /**
   * Elementos com a mesma descrição nas duas colunas (E117.F1, ADR-0079 E.19): resolvido no
   * servidor. Opcional na leitura: a resposta do parecer recém-gravado não o traz.
   */
  colunas_identicas?: number;
  /**
   * E123.F2 (ADR-0079 E.24): a conferência foi pedida pelo sistema — quando o par se formou ou o
   * perito trocou uma foto —, e não pelo botão. Opcional na leitura: o parecer antigo não a traz.
   */
  conferencia_automatica?: boolean;
  /**
   * E172.F4 (B2.3): a qualidade de cada imagem, avaliada antes da comparação. `null` no parecer
   * anterior; opcional na leitura.
   */
  qualidade?: QualidadeDoParData | null;
}

/** A contagem da consolidação pela maioria (espelha `types/conferencia_facial_sustentacao.py`). */
export interface SustentacaoDasLeiturasData {
  regra: 'maioria';
  k: number;
  leitura: number;
  compatibilidade: number;
  por_elemento: Record<string, number>;
  sem_maioria: string[];
}

/**
 * A contagem de um ponto sem unanimidade — "2 de 3" ou a falta de maioria —, ou `null` quando as K
 * leituras coincidiram ou o parecer é anterior à maioria (E.13).
 */
export function contagemDoPonto(
  sustentacao: SustentacaoDasLeiturasData | null | undefined,
  ponto: string,
): { n: number; k: number } | 'sem_maioria' | null {
  if (sustentacao === null || sustentacao === undefined) return null;
  if (sustentacao.sem_maioria.includes(ponto)) return 'sem_maioria';
  const n =
    ponto === 'leitura'
      ? sustentacao.leitura
      : ponto === 'compatibilidade'
        ? sustentacao.compatibilidade
        : (sustentacao.por_elemento[ponto] ?? 0);
  return n === sustentacao.k ? null : { n, k: sustentacao.k };
}

export interface ConferenciaFacialData {
  case_id: string;
  tem_par_eleito: boolean;
  leitura: LeituraDaConferencia | null;
  parecer: ConferenciaFacialParecerData | null;
  /** O perito trocou a foto escolhida depois do parecer. */
  par_mudou: boolean;
  /** E141.F1 (ADR-0079 E.26): o mesmo par, com outra caixa ou outro giro desde o parecer. */
  recorte_mudou?: boolean;
  /** E123.F2: a conferência automática está rodando — a tela relê enquanto for verdadeiro. */
  em_curso?: boolean;
}

function basePath(caseId: string): string {
  return `/casos/${caseId}/conferencia-facial`;
}

const LEITURAS_DO_PARECER: readonly string[] = [
  'elementos_convergentes',
  'elementos_divergentes',
  'nao_foi_possivel_conferir',
];

const LEITURAS_DA_TELA: readonly string[] = [
  ...LEITURAS_DO_PARECER,
  'nao_se_aplica',
  'nao_demonstrada',
];

const SHA256_HEX = /^[0-9a-f]{64}$/;

const ELEMENTOS: readonly string[] = ELEMENTOS_DO_PAINEL;
const GRAUS: readonly string[] = GRAUS_DA_LINHA;
const DEGRAUS: readonly string[] = COMPATIBILIDADES_DO_PAR;

/** `null` é o parecer sem degrau; qualquer outra coisa fora da escala é drift. */
function ehCompatibilidade(valor: unknown): boolean {
  return valor === null || (typeof valor === 'string' && DEGRAUS.includes(valor));
}

function objeto(valor: unknown): Record<string, unknown> | null {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor)
    ? (valor as Record<string, unknown>)
    : null;
}

function listaDeTexto(valor: unknown): boolean {
  return Array.isArray(valor) && valor.every((item) => typeof item === 'string');
}

function ehFotoConferida(valor: unknown, grupo: GrupoDaFoto): boolean {
  const foto = objeto(valor);
  return (
    foto !== null &&
    typeof foto.foto_id === 'string' &&
    foto.grupo === grupo &&
    typeof foto.nome_arquivo === 'string' &&
    typeof foto.content_sha256 === 'string' &&
    SHA256_HEX.test(foto.content_sha256)
  );
}

/**
 * O painel vem com os NOVE na ordem, ou vazio — nada entre as duas coisas.
 *
 * A mesma régua que o servidor impõe nas duas fronteiras (F-E87.F1-01): painel
 * parcial, duplicado ou permutado é resposta fora do contrato. Aceitar aqui o
 * que o servidor recusa lá deixaria a tela desenhar meio painel e chamar isso
 * de parecer.
 */
function ehPainel(valor: unknown): boolean {
  if (!Array.isArray(valor)) return false;
  if (valor.length === 0) return true;
  // E172.F4 (B2.3): a lista completa; o parecer anterior, com os nove, é lido como foi.
  const forma: readonly string[] | undefined = [ELEMENTOS, ELEMENTOS_DO_PAINEL_V1].find(
    (lista) => lista.length === valor.length,
  );
  if (forma === undefined) return false;
  return valor.every((item, indice) => {
    const linha = objeto(item);
    return (
      linha !== null &&
      linha.elemento === forma[indice] &&
      typeof linha.selfie === 'string' &&
      typeof linha.identidade === 'string' &&
      typeof linha.grau === 'string' &&
      GRAUS.includes(linha.grau)
    );
  });
}

function textosEm(alvo: Record<string, unknown>, chaves: readonly string[]): boolean {
  return chaves.every((chave) => typeof alvo[chave] === 'string');
}

function ehParDoParecer(valor: unknown): boolean {
  const par = objeto(valor);
  return (
    par !== null &&
    ehFotoConferida(par.documento_identidade, 'documento_identidade') &&
    ehFotoConferida(par.ccb, 'ccb')
  );
}

function ehParecer(valor: unknown): valor is ConferenciaFacialParecerData {
  const parecer = objeto(valor);
  if (parecer === null) return false;
  return (
    textosEm(parecer, ['id', 'case_id', 'created_by', 'created_at', 'leitura']) &&
    LEITURAS_DO_PARECER.includes(parecer.leitura as string) &&
    ['convergencias', 'divergencias', 'ressalvas'].every((campo) => listaDeTexto(parecer[campo])) &&
    ehPainel(parecer.painel) &&
    ehCompatibilidade(parecer.compatibilidade) &&
    ehParDoParecer(parecer.par)
  );
}

/**
 * Resposta que não é o contrato INTEIRO vira erro, e não um objeto meio-vazio.
 *
 * A primeira versão desta guarda conferia só `tem_par_eleito` (F-E80.F2-07), e
 * isso não continha nada: um 200 parcial chegava com `parecer: undefined`, que
 * `undefined !== null` encaminhava ao componente do parecer, e o acesso a
 * `parecer.leitura` derrubava a PÁGINA no Error Boundary — exatamente o cenário
 * que a guarda dizia manter dentro da seção. Drift de versão devolve um corpo
 * plausível, não um corpo obviamente errado; conferir um campo só é o mesmo que
 * não conferir.
 */
function ehContrato(data: unknown): data is ConferenciaFacialData {
  const estado = objeto(data);
  if (estado === null) return false;
  const leituraValida =
    estado.leitura === null ||
    (typeof estado.leitura === 'string' && LEITURAS_DA_TELA.includes(estado.leitura));
  return (
    typeof estado.case_id === 'string' &&
    typeof estado.tem_par_eleito === 'boolean' &&
    typeof estado.par_mudou === 'boolean' &&
    leituraValida &&
    (estado.parecer === null || ehParecer(estado.parecer))
  );
}

export async function lerConferenciaFacial(
  client: AxiosInstance,
  caseId: string,
): Promise<ConferenciaFacialData> {
  let data: unknown;
  try {
    ({ data } = await client.get<ConferenciaFacialData>(basePath(caseId)));
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
  if (!ehContrato(data)) throw classifyExameEletronicoError(new Error('resposta fora do contrato'));
  return data;
}

/**
 * Compara o par escolhido e devolve o parecer recém-gravado.
 *
 * O retorno é validado como o do GET: o corpo da escrita alimenta a mesma tela,
 * e deixá-lo passar sem conferência só mudaria de porta o objeto meio-vazio.
 */
export const CONFERENCIA_FACIAL_TIMEOUT_MS = 190_000;
/** Orçamento da chamada: três execuções em série, cada uma com até 60 s no
 * servidor (`conferencia_facial_assistant`), mais margem. O padrão do cliente
 * (15 s) cortava a conexão no meio da primeira execução — o servidor concluía e
 * gravava o parecer, e a tela mostrava erro (E82.F5, medido em homolog). */

export async function executarConferenciaFacial(
  client: AxiosInstance,
  caseId: string,
): Promise<ConferenciaFacialParecerData> {
  let data: unknown;
  try {
    ({ data } = await client.post<ConferenciaFacialParecerData>(basePath(caseId), undefined, {
      timeout: CONFERENCIA_FACIAL_TIMEOUT_MS,
    }));
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
  if (!ehParecer(data)) throw classifyExameEletronicoError(new Error('resposta fora do contrato'));
  return data;
}

/**
 * E172.F4 (B2.3): os itens que o assistente técnico verifica neste parecer — a qualidade de cada
 * imagem, quando o parecer a registrou, e cada componente do painel, na ordem. A mesma régua do
 * servidor (`itens_da_verificacao`).
 */
export function itensDaVerificacao(parecer: ConferenciaFacialParecerData): string[] {
  const qualidade = parecer.qualidade ? [...ITENS_DE_QUALIDADE] : [];
  return [...qualidade, ...parecer.painel.map((linha) => linha.elemento)];
}
